import { createAsyncThunk } from "@reduxjs/toolkit";
import { createFormSlice, fetchLatestDataOrDefault } from "../../../shared/slice";
import { myCompaniesApi } from "../../../../../../../../../services/accounting/myCompany/api";
import { countriesApi } from "../../../../../../../../../services/accounting/country/api";
import { defaults } from "../../../../../../../../../utils/constants";
import { departmentsApi } from "../../../../../../../../../services/accounting/department/api";
import { pettyCashesApi } from "../../../../../../../../../services/accounting/pettyCash/api";
import dateFormat from "../../../../../../../../../shared/utils/dateFormat";
import { AppDispatch, GetRootState } from "../../../../../../../../../shared/redux/types";
import { Data } from "../../../../../../../../../services/dataEntering/data/types";
import { MyCompany } from "../../../../../../../../../services/accounting/myCompany/types";
import { Country } from "../../../../../../../../../services/accounting/country/types";
import { Department } from "../../../../../../../../../services/accounting/department/types";
import { Contact } from "../../../../../../../../../services/accounting/contact/types";
import { contactsApi } from "../../../../../../../../../services/accounting/contact/api";
import { Item, PettyCashFormState } from "./types";
import { PettyCash } from "../../../../../../../../../services/accounting/pettyCash/types";

async function fetchDefaultData(
    accountId: number,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<{
    data: Data;
    myCompany: MyCompany;
    defaultCountry: Country;
    defaultDepartment: Department;
    defaultPettyCash: PettyCash;
    contact: Contact | null;
}> {
    const [dataResponse, myCompany, defaultDepartment] = await Promise.all([
        fetchLatestDataOrDefault(dispatch, getState),
        myCompaniesApi.endpoints.getLatestMyCompany
            .initiate({ accountId })(dispatch, getState, null)
            .unwrap(),
        departmentsApi.endpoints.getDefaultDepartment
            .initiate({ accountId })(dispatch, getState, null)
            .unwrap(),
    ]);

    const { data } = dataResponse;
    const defaultCountry = await countriesApi.endpoints.getCountry
        .initiate({
            accountId,
            countryId: myCompany?.country?.countryId || defaults.invoices.country,
        })(dispatch, getState, null)
        .unwrap();

    const { currencyId } = defaultCountry;
    const defaultPettyCash = await pettyCashesApi.endpoints.getDefaultPettyCash
        .initiate({ accountId, currencyId })(dispatch, getState, null)
        .unwrap();
    let contact: Contact = null;

    if (data?.counterparty?.contactId) {
        const { data: contactData } = await contactsApi.endpoints.getContact.initiate({
            accountId,
            contactId: data.counterparty.contactId,
        })(dispatch, getState, null);
        contact = contactData;
    }

    return { data, myCompany, defaultCountry, defaultDepartment, contact, defaultPettyCash };
}

const defaultItem: Item = {
    rule: null,
    description: "",
    price: 0,
};

const transformItems = (dataItems: Data["items"]): Item[] => {
    const transformed = dataItems.map((item) => ({
        rule: null,
        description: item.description || "",
        price: Number(item.totalPrice) || 0,
    }));

    return transformed.length > 0 ? transformed : [defaultItem];
};

export const setDefaults = createAsyncThunk(
    "pettyCashIncomeForm/setDefaults",
    async (accountId: number, { dispatch, getState, rejectWithValue }) => {
        try {
            const { data, defaultDepartment, contact, defaultPettyCash } = await fetchDefaultData(
                accountId,
                dispatch as AppDispatch,
                getState as GetRootState,
            );

            const items = transformItems(data.items);

            return {
                contact: contact,
                pettyCashId: defaultPettyCash?.pettyCashId,
                issueDate: dateFormat.todayInInputFormat(),
                note: data?.note || "",
                contract: null,
                department: defaultDepartment,
                items,
            } as PettyCashFormState;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

const slice = createFormSlice("pettyCashIncomeForm", (builder) => {
    builder
        .addCase(setDefaults.fulfilled, (draftState, action) => {
            draftState.data = action.payload;
            draftState.status = "initialized";
        })
        .addCase(setDefaults.pending, (draftState) => {
            draftState.data = {};
            draftState.status = "initializing";
        })
        .addCase(setDefaults.rejected, (draftState) => {
            draftState.data = {};
            draftState.status = "error";
        });
});

export const { name, actions, reducer } = slice;
